import { logger } from "@/utils/logger";
import { securedSupabase } from '@/integrations/supabase/secured-client';
import { getDealerID } from './dealerDataUtils';
import { DealerManager } from './dealerManager';
import { getCookie } from './cookieUtils';
import { AutoLeadSubmission } from './autoLeadSubmission';
import { getCurrentSession } from './sessionManager';
import { toast } from 'sonner';

// Known automotive makes — used to guard against garbage VOI strings
// (e.g. legal text scraped from page context). Lowercase comparison.
const KNOWN_VEHICLE_MAKES = new Set([
  'acura','alfa romeo','aston martin','audi','bentley','bmw','buick','cadillac',
  'chevrolet','chevy','chrysler','dodge','ferrari','fiat','ford','genesis','gmc',
  'honda','hummer','hyundai','infiniti','jaguar','jeep','kia','lamborghini',
  'land rover','lexus','lincoln','lotus','maserati','mazda','mclaren','mercedes',
  'mercedes-benz','mini','mitsubishi','nissan','polestar','pontiac','porsche',
  'ram','rivian','rolls-royce','saab','saturn','scion','smart','subaru','suzuki',
  'tesla','toyota','volkswagen','vw','volvo'
]);

/**
 * Guard against malformed vehicle data leaking into ADF payloads.
 * Returns true when (make, model) look like a real vehicle.
 * Examples rejected: make="Business" model="Corporations Act (Alberta), RSA 2000, c B-9"
 */
const isPlausibleVehicle = (make?: string, model?: string): boolean => {
  if (!make || !model) return false;
  const makeLower = make.trim().toLowerCase();
  if (!KNOWN_VEHICLE_MAKES.has(makeLower)) return false;
  const modelTrim = model.trim();
  if (modelTrim.length === 0 || modelTrim.length > 40) return false;
  // Reject obvious non-model text (commas, legal/statute keywords, year-like 4-digit blocks)
  if (/,/.test(modelTrim)) return false;
  if (/\b(act|rsa|rsc|chapter|section|clause|statute|corporation|corporations)\b/i.test(modelTrim)) return false;
  return true;
};


interface ADFVehicle {
  interest: 'buy' | 'trade-in';
  status: 'New' | 'Used' | 'new' | 'used';
  id?: string;
  year?: string | number;
  vehicletype?: string;
  make?: string;
  model?: string;
  trim?: string;
  vin?: string;
  stock?: string;
  price?: string | number;
  condition?: 'new' | 'used';
  odometer?: {
    value: string | number;
    status: 'original' | 'replaced';
    units: 'km' | 'miles';
  };
}

interface ADFContact {
  name: string;
  phone?: string;
  email?: string;
  address?: {
    postalcode?: string;
    regioncode?: string;
    country?: string;
  };
}

interface ADFLeadParams {
  prospect: {
    id?: string;
    language?: string;
  };
  vehicles?: ADFVehicle[];
  customer: ADFContact;
  comments?: string;
  formSubmission?: any;
  interestData?: any;
  chatTranscription?: any[];
  formType?: string;
  formEmailsData?: {
    htmlEmail: string;
    adfEmail: string;
    department: string;
  } | null;
  conversationSummary?: string;
}

export const pushADFLead = async (params: ADFLeadParams): Promise<{ success: boolean; leadId?: string }> => {
  logger.log('🔥🔥🔥 === ADF LEAD PUSH STARTING ===');
  logger.log('🔥🔥🔥 Input params:', JSON.stringify(params, null, 2));
  logger.log('🟢 STEP 7 - pushADFLead received params.interestData:', JSON.stringify(params.interestData, null, 2));
  logger.log('🟢 STEP 7 - params.interestData.message:', params.interestData?.message);
  logger.log('🔥🔥🔥 FormEmailsData received:', JSON.stringify(params.formEmailsData, null, 2));
  logger.log('=== ADF LEAD PUSH STARTING ===');
  logger.log('Input params:', JSON.stringify(params, null, 2));

  try {
    // ============================================
    // UNIFIED DEDUPLICATION CHECK
    // Prevents duplicate leads from ALL entry points
    // ============================================
    const session = getCurrentSession();
    const sessionId = params.prospect?.id || session?.sessionId || `session_${Date.now()}`;
    
    // Build contact object for dedup check
    const contactForDedup = {
      Name: params.customer?.name,
      Phone: params.customer?.phone,
      Email: params.customer?.email
    };
    
    // Check if this lead was already submitted in this session
    const alreadySubmitted = await AutoLeadSubmission.hasBeenSubmitted(sessionId, contactForDedup);
    
    if (alreadySubmitted) {
      logger.log('🚫🚫🚫 DUPLICATE LEAD BLOCKED - Already submitted in this session');
      logger.log('🚫🚫🚫 Session:', sessionId);
      logger.log('🚫🚫🚫 Contact:', contactForDedup);
      logger.log('🚫 Duplicate lead blocked - already submitted in session:', sessionId);
      return { success: true }; // Return true since lead was already successfully submitted before
    }
    
    logger.log('✅ Dedup check passed - lead has NOT been submitted yet');
    // ============================================

    // Get dealer information
    const dealerId = await getDealerID();
    logger.log('🔥🔥🔥 ADF Dealer ID:', dealerId);
    if (!dealerId || dealerId === 0) {
      logger.log('🔥🔥🔥 ❌ No valid dealer ID, skipping ADF lead push');
      logger.log('❌ No valid dealer ID, skipping ADF lead push');
      return { success: false };
    }

    // Get ADF-specific dealer data using secured function call.
    // This same call also mints the short-lived, single-use lead grant for the
    // form-submission path (no extra round-trip), scoped to this session/dealer.
    const { data: dealerDataResult, error: dealerError } = await securedSupabase.raw
      .rpc('get_dealer_adf_info', { dealer_id_param: dealerId, p_session_id: sessionId });

    if (dealerError || !dealerDataResult) {
      logger.error('❌ Failed to fetch dealer ADF data:', dealerError);
      return { success: false };
    }

    // Parse the JSON response
    const dealerData = dealerDataResult as { 
      success: boolean; 
      send_adf_leads_to?: string; 
      domain?: string; 
      dealer_communication_language?: string;
      dealer_name?: string;
      lead_grant_token?: string | null;
      lead_grant_expires_at?: string | null;
      lead_grant_reason?: string;
      error?: string; 
    };

    
    if (!dealerData.success) {
      logger.error('❌ Dealer ADF data fetch failed:', dealerData.error);
      return { success: false };
    }

    // Determine recipient email - use extracted form emails first, then fallback to dealer config
    let recipientEmail = '';
    logger.log('🔥🔥🔥 ADF Email routing - formEmailsData:', JSON.stringify(params.formEmailsData, null, 2));
    // ADF Email routing configured
    
    if (params.formEmailsData?.adfEmail) {
      recipientEmail = params.formEmailsData.adfEmail;
      logger.log('🔥🔥🔥 ✅ Using extracted ADF email:', recipientEmail);
    } else if (dealerData.send_adf_leads_to) {
      recipientEmail = dealerData.send_adf_leads_to;
      logger.log('🔥🔥🔥 ✅ Using dealer configured ADF email:', recipientEmail);
    } else {
      // No ADF email configured - D2C API will still be called, but no email will be sent
      logger.log('🔥🔥🔥 ℹ️ No dealer ADF email configured, D2C API will be called but no email notification will be sent');
      logger.log('ℹ️ No ADF email configured - D2C API call will proceed, email notification skipped');
    }

    // Generate ADF XML
    const adfXml = await generateADFXML(params, dealerData);
    logger.log('Generated ADF XML:', adfXml);

    // Use base supabase client to avoid dealer context headers interfering with edge functions
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Read UTM cookies for campaign and source data
    const utmSource = getCookie('utm_source');
    const utmCampaign = getCookie('utm_campaign');
    
    // Use already computed sessionId from dedup check
    
    // Extract last name for anonymized logging (only first initial will be stored)
    const customerName = params.customer?.name || '';
    const nameParts = customerName.trim().split(' ');
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
    
    // Get dealer name for logging
    const dealerName = dealerData.dealer_name || dealerData.domain || '';
    
    // Get AI summary from interest data or conversation summary
    const aiSummary = params.conversationSummary || params.interestData?.message || '';
    
    // Resolve the single-use lead grant:
    //  1) inline grant minted by the streaming AI response (AI Contact path)
    //  2) grant returned by get_dealer_adf_info above (form-submission path / non-stream dealers)
    const { consumeLeadGrant } = await import('./leadGrantStore');
    const leadGrantToken =
      consumeLeadGrant(sessionId, Number(dealerId)) || dealerData.lead_grant_token || '';

    if (!leadGrantToken) {
      // Observability: surface WHY the grant was refused (never log the token itself).
      // The matching server-side detail (incl. the origin/referer Postgres actually saw)
      // is logged by mint_adf_lead_grant as "[adf_lead_grant] refused ...".
      logger.error('❌ ADF lead push aborted - no valid lead grant', {
        dealer_id: dealerId,
        session_id: sessionId,
        reason: dealerData.lead_grant_reason || 'unknown',
        had_inline_stream_grant: false,
        page_origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
      });
      return { success: false };
    }


    // Send ADF lead via edge function
    logger.log('🔥🔥🔥 📡 About to invoke send-adf-lead edge function...');
    const { data, error } = await supabase.functions.invoke('send-adf-lead', {
      headers: {
        'x-lead-grant': leadGrantToken,
      },
      body: {
        dealerId: dealerId,
        adfXml: adfXml,
        recipientEmail: recipientEmail,
        formType: params.formType || 'Unknown',
        campaign: utmCampaign || undefined,
        source: utmSource || undefined,
        sessionId: sessionId || undefined,
        lastName: lastName || undefined,
        aiSummary: aiSummary ? aiSummary.substring(0, 500) : undefined, // Limit summary length
        dealerName: dealerName || undefined,
        // Pass contact info for server-side duplicate detection (will be hashed)
        contactName: params.customer?.name || undefined,
        contactPhone: params.customer?.phone || undefined,
        contactEmail: params.customer?.email || undefined
      }
    });


    logger.log('🔥🔥🔥 📨 Edge function response received');
    logger.log('🔥🔥🔥 📨 Data:', JSON.stringify(data, null, 2));
    logger.log('🔥🔥🔥 📨 Error:', JSON.stringify(error, null, 2));

    if (error) {
      logger.error('❌ ADF lead push error:', error);
      console.error('🔥🔥🔥 ❌ Edge function error details:', error);
      return { success: false };
    }

    if (!data || !data.success) {
      logger.error('❌ ADF lead push failed - no success in response:', data);
      console.error('🔥🔥🔥 ❌ Edge function returned non-success response');
      return { success: false };
    }

    logger.log('✅ ADF lead pushed successfully:', data);
    logger.log('🔥🔥🔥 ✅ ADF lead push completed successfully, leadId:', data.leadId);

    // Fire Google Ads conversion pixel if Ads data exists in cookie
    try {
      const { getD2CChatbotCookie } = await import('./cookieUtils');
      const { ga4Tracker } = await import('./ga4Tracking');
      const d2cCookie = getD2CChatbotCookie();
      if (d2cCookie?.ads?.id && d2cCookie?.ads?.label) {
        ga4Tracker.trackGoogleAdsConversion(d2cCookie.ads.id, d2cCookie.ads.label);
        logger.log('✅ Google Ads conversion fired with ID:', d2cCookie.ads.id, 'Label:', d2cCookie.ads.label);
      }
    } catch (adsError) {
      logger.error('Failed to fire Google Ads conversion:', adsError);
    }
    
    // Mark as submitted to prevent future duplicates
    await AutoLeadSubmission.markAsSubmitted(sessionId, contactForDedup);
    logger.log('🔥🔥🔥 ✅ Lead marked as submitted in session tracker');
    
    return { success: true, leadId: data.leadId || undefined };

  } catch (error) {
    logger.error('💥 Exception in ADF lead push:', error);
    return { success: false };
  }
};

// Helper function to capitalize form type
const capitalizeFormType = (formType: string): string => {
  if (!formType) return 'Unknown';
  
  // Special handling for specific form types
  const specialCases: { [key: string]: string } = {
    'financev1': 'FinanceV1',
    'finance': 'Finance',
    'contact': 'Contact',
    'appointment': 'Appointment',
    'offers': 'Offers',
    'service': 'Service',
    'carfax': 'Carfax'
  };
  
  const lowerType = formType.toLowerCase();
  return specialCases[lowerType] || formType.charAt(0).toUpperCase() + formType.slice(1).toLowerCase();
};
// Build comprehensive CRM comments in markdown format
const buildCRMComments = (params: ADFLeadParams, language: string): string => {
  const sections: string[] = [];
  const isFrench = language.toLowerCase().includes('fr');
  
  // Header
  sections.push(isFrench ? '## Résumé de l\'opportunité' : '## Opportunity Summary');
  sections.push('');
  
  // AI Summary section (most important for CRM user)
  if (params.interestData?.message || params.conversationSummary) {
    sections.push(isFrench ? '### Résumé AI' : '### AI Summary');
    if (params.interestData?.message) {
      sections.push(params.interestData.message);
    }
    if (params.conversationSummary) {
      sections.push('');
      sections.push(params.conversationSummary);
    }
    sections.push('');
  }
  
  // Contact Information section
  if (params.customer) {
    sections.push(isFrench ? '### Coordonnées' : '### Contact Information');
    if (params.customer.name) {
      sections.push(`- **${isFrench ? 'Nom' : 'Name'}:** ${params.customer.name}`);
    }
    if (params.customer.phone) {
      sections.push(`- **${isFrench ? 'Téléphone' : 'Phone'}:** ${params.customer.phone}`);
    }
    if (params.customer.email) {
      sections.push(`- **${isFrench ? 'Courriel' : 'Email'}:** ${params.customer.email}`);
    }
    if (params.customer.address?.postalcode) {
      sections.push(`- **${isFrench ? 'Code postal' : 'Postal Code'}:** ${params.customer.address.postalcode}`);
    }
    sections.push('');
  }
  
  // Vehicle Interest section - extract from interestData
  if (params.interestData) {
    const interest = params.interestData;
    const hasVehicleData = interest.Year || interest.Make || interest.Model || 
                           interest.VIN || interest.Stock || interest.Vehicle ||
                           interest.vehicleOfInterest;
    
    if (hasVehicleData) {
      sections.push(isFrench ? '### Véhicule d\'intérêt' : '### Vehicle of Interest');
      
      // Build vehicle string
      const vehicleParts: string[] = [];
      if (interest.Year) vehicleParts.push(interest.Year.toString());
      if (interest.Make) vehicleParts.push(interest.Make);
      if (interest.Model) vehicleParts.push(interest.Model);
      if (interest.Trim || interest.Version) vehicleParts.push(interest.Trim || interest.Version);
      
      if (vehicleParts.length > 0) {
        sections.push(`- **${isFrench ? 'Véhicule' : 'Vehicle'}:** ${vehicleParts.join(' ')}`);
      } else if (interest.Vehicle) {
        sections.push(`- **${isFrench ? 'Véhicule' : 'Vehicle'}:** ${interest.Vehicle}`);
      } else if (interest.vehicleOfInterest) {
        sections.push(`- **${isFrench ? 'Véhicule' : 'Vehicle'}:** ${interest.vehicleOfInterest}`);
      }
      
      if (interest.VIN) {
        sections.push(`- **VIN:** ${interest.VIN}`);
      }
      if (interest.Stock || interest.Stock_Number) {
        sections.push(`- **Stock #:** ${interest.Stock || interest.Stock_Number}`);
      }
      if (interest.Price) {
        sections.push(`- **${isFrench ? 'Prix' : 'Price'}:** ${interest.Price}`);
      }
      if (interest.Mileage) {
        sections.push(`- **${isFrench ? 'Kilométrage' : 'Mileage'}:** ${interest.Mileage}`);
      }
      if (interest.Status) {
        sections.push(`- **Status:** ${interest.Status}`);
      }
      sections.push('');
    }
    
    // Additional interest data fields
    const additionalFields: string[] = [];
    if (interest.Budget || interest.monthlyBudget) {
      additionalFields.push(`- **${isFrench ? 'Budget' : 'Budget'}:** ${interest.Budget || interest.monthlyBudget}`);
    }
    if (interest.TradeIn || interest.hasTradeIn) {
      additionalFields.push(`- **${isFrench ? 'Échange' : 'Trade-In'}:** ${isFrench ? 'Oui' : 'Yes'}`);
    }
    if (interest.PreferredContact) {
      additionalFields.push(`- **${isFrench ? 'Contact préféré' : 'Preferred Contact'}:** ${interest.PreferredContact}`);
    }
    if (interest.AppointmentDate || interest.preferredDate) {
      additionalFields.push(`- **${isFrench ? 'Date préférée' : 'Preferred Date'}:** ${interest.AppointmentDate || interest.preferredDate}`);
    }
    if (interest.AppointmentTime || interest.preferredTime) {
      additionalFields.push(`- **${isFrench ? 'Heure préférée' : 'Preferred Time'}:** ${interest.AppointmentTime || interest.preferredTime}`);
    }
    
    if (additionalFields.length > 0) {
      sections.push(isFrench ? '### Préférences' : '### Preferences');
      sections.push(...additionalFields);
      sections.push('');
    }
  }
  
  // Form type and source
  if (params.formType) {
    sections.push(`---`);
    sections.push(`*${isFrench ? 'Type de formulaire' : 'Form Type'}: ${params.formType}*`);
  }
  
  // If nothing was added, provide a fallback
  if (sections.length <= 2) {
    return isFrench 
      ? 'Demande soumise via chatbot D2C' 
      : 'Lead submitted via D2C chatbot';
  }
  
  return sections.join('\n');
};

export const generateADFXML = async (params: ADFLeadParams, dealerData: any): Promise<string> => {
  const now = new Date();
  const requestDate = now.toISOString().replace('Z', '-05:00'); // EST timezone
  const language = params.prospect.language || (dealerData.dealer_communication_language === 'Fr' ? 'Français' : 'English');
  
  // Parse vehicle information from form submission
  let vehiclesXml = '';
  let year = '';
  let make = '';
  let model = '';
  let trim = '';
  let vin = '';
  let stock = '';
  let status = '';
  let price = '';
  let mileage = '';
  let color = '';
  
  // PRIORITY 1: Check form submission data first (most current and accurate)
  if (params.formSubmission) {
    logger.log('🔥🔥🔥 === CHECKING FORM SUBMISSION DATA FIRST ===');
    logger.log('🔥🔥🔥 Form submission data:', JSON.stringify(params.formSubmission, null, 2));
    
    // Direct field mapping from form submission
    year = params.formSubmission.Year || params.formSubmission.year || '';
    make = params.formSubmission.Make || params.formSubmission.make || '';
    model = params.formSubmission.Model || params.formSubmission.model || '';
    trim = params.formSubmission.Trim || params.formSubmission.trim || '';
    vin = params.formSubmission.VIN || params.formSubmission.vin || '';
    stock = params.formSubmission['Stock #'] || params.formSubmission.stock || '';
    price = params.formSubmission['vehicle.price'] || params.formSubmission.price || '';
    mileage = params.formSubmission.Mileage || params.formSubmission.mileage || '';
    
    // Extract both interior and exterior color information
    const exteriorColor = params.formSubmission['Exterior Color'] || params.formSubmission.Color || params.formSubmission.color || '';
    const interiorColor = params.formSubmission['Interior Color'] || params.formSubmission.interiorColor || '';
    color = exteriorColor || interiorColor; // For backward compatibility
    
    // Determine status based on year and mileage
    const currentYear = new Date().getFullYear();
    const vehicleYear = parseInt(year);
    
    if (vehicleYear && vehicleYear < currentYear) {
      status = 'used';
      logger.log(`🔥🔥🔥 Status determined from year: ${vehicleYear} < ${currentYear} -> used`);
    } else if (mileage) {
      const mileageNum = parseFloat(mileage.toString().replace(/[^\d.]/g, ''));
      status = mileageNum > 100 ? 'used' : 'new'; // Lower threshold for new vehicles
      logger.log(`🔥🔥🔥 Status determined from mileage: ${mileage} -> ${mileageNum}km -> ${status}`);
    } else {
      status = vehicleYear === currentYear ? 'new' : 'used';
      logger.log(`🔥🔥🔥 Status defaulted based on year: ${vehicleYear} -> ${status}`);
    }
    
    if (year || make || model) {
      logger.log('🔥🔥🔥 ✅ Vehicle data extracted from form submission:', { year, make, model, trim, vin, stock, price, mileage, color, status });
    }
  }
  
  // PRIORITY 2: Fallback to centralized storage if form submission incomplete
  if (!year && !make && !model) {
    try {
      logger.log('🔥🔥🔥 === FALLBACK TO CENTRALIZED STORAGE ===');
      
      const { getStoredInterestData } = await import('./interestDataUtils');
      const storedInterest = await getStoredInterestData();
      logger.log('🔥🔥🔥 Centralized vehicle data lookup:', JSON.stringify(storedInterest, null, 2));
      
      if (storedInterest?.vehicle) {
        const vehicleData = storedInterest.vehicle;
        year = year || vehicleData.year?.toString() || '';
        make = make || vehicleData.make || '';
        model = model || vehicleData.model || '';
        trim = trim || vehicleData.trim || vehicleData.version || '';
        vin = vin || vehicleData.vin || vehicleData.VIN || '';
        stock = stock || vehicleData.stock || '';
        price = price || (vehicleData.price ? vehicleData.price.toString() : '');
        mileage = mileage || (vehicleData.mileage ? vehicleData.mileage.toString() : '');
        color = color || vehicleData.color || '';
        status = status || vehicleData.status || '';
        
        logger.log('🔥🔥🔥 ✅ Vehicle data extracted from centralized storage:', { year, make, model, trim, vin, stock, price, mileage, color, status });
      } else {
        logger.log('🔥🔥🔥 ❌ No vehicle data found in centralized storage');
      }
    } catch (e) {
      logger.log('🔥🔥🔥 Exception parsing localStorage:', e);
    }
  }
  
  // Fallback: try to extract vehicle data from interestData in comments  
  if (!year && !make && !model && params.comments) {
    const interestDataMatch = params.comments.match(/interestData[^}]*:\s*({[^}]+})/);
    if (interestDataMatch) {
      try {
        const interestData = JSON.parse(interestDataMatch[1]);
        year = interestData.year || '';
        make = interestData.make || '';
        model = interestData.model || '';
        trim = interestData.version || interestData.trim || '';
        vin = interestData.vin || '';
        stock = interestData.stock || '';
        status = interestData.status || '';
        logger.log('🔥🔥🔥 Vehicle data extracted from comments interestData:', { year, make, model, trim, vin, stock, status });
      } catch (e) {
        logger.log('Failed to parse interestData from comments');
      }
    }
  }
  
  // Fallback: try to get vehicle info from Interest object in form submission
  if (!year && !make && !model && params.formSubmission && params.formSubmission.Interest) {
    logger.log('🔥🔥🔥 Parsing Interest object:', JSON.stringify(params.formSubmission.Interest, null, 2));
    year = params.formSubmission.Interest.year || '';
    make = params.formSubmission.Interest.make || '';
    model = params.formSubmission.Interest.model || '';
    trim = params.formSubmission.Interest.version || params.formSubmission.Interest.trim || '';
    vin = params.formSubmission.Interest.vin || '';
    stock = params.formSubmission.Interest.stock || '';
    status = params.formSubmission.Interest.status || '';
    logger.log('🔥🔥🔥 Vehicle data extracted from Interest:', { year, make, model, trim, vin, stock, status });
  }
  
  // NEW: Enhanced fallback to extract vehicle data from comment/transcription content
  if ((!model || !year || !trim) && params.comments) {
    logger.log('🔥🔥🔥 === ENHANCED VEHICLE EXTRACTION FROM COMMENTS ===');
    logger.log('🔥🔥🔥 Comments content:', params.comments);
    
    // Try to extract vehicle data from various patterns in comments
    const vehiclePatterns = [
      // Pattern: "Kia Seltos LX 2025"
      /\b(Kia|Toyota|Honda|Ford|Chevrolet|GMC|Hyundai|Nissan|Mazda|Subaru|Jeep|Ram|Dodge)\s+([A-Za-z]+)(?:\s+(LX|EX|SE|Limited|Sport|Premium|S|SV|SR|XLE|LE|Base))?\s+(\d{4})\b/gi,
      // Pattern: "2025 Kia Seltos LX"
      /\b(\d{4})\s+(Kia|Toyota|Honda|Ford|Chevrolet|GMC|Hyundai|Nissan|Mazda|Subaru|Jeep|Ram|Dodge)\s+([A-Za-z]+)(?:\s+(LX|EX|SE|Limited|Sport|Premium|S|SV|SR|XLE|LE|Base))?\b/gi,
      // Pattern: JSON-like structure in comments
      /"make":\s*"([^"]+)"|"model":\s*"([^"]+)"|"year":\s*"?(\d{4})"?|"trim":\s*"([^"]+)"/gi
    ];
    
    for (const pattern of vehiclePatterns) {
      let match;
      pattern.lastIndex = 0; // Reset regex state
      
      while ((match = pattern.exec(params.comments)) !== null) {
        logger.log('🔥🔥🔥 Found vehicle pattern match:', match);
        
        if (pattern.source.includes('d{4}')) {
          // Pattern with year first: [year, make, model, trim?]
          if (match[1] && /^\d{4}$/.test(match[1])) {
            year = year || match[1];
            make = make || match[2];
            model = model || match[3];
            trim = trim || match[4] || '';
          } else {
            // Pattern with make first: [make, model, trim?, year]
            make = make || match[1];
            model = model || match[2]; 
            trim = trim || match[3] || '';
            year = year || match[4];
          }
        }
        
        logger.log('🔥🔥🔥 Updated vehicle data from pattern:', { year, make, model, trim });
        
        // Break after first successful match with make/model
        if (make && model) break;
      }
      
      // Break out of pattern loop if we found make/model
      if (make && model) break;
    }
    
    // Additional pattern for extracting from form submission strings
    const formSubmissionMatch = params.comments.match(/FORM_SUBMISSION:\{[^}]*"formType":\s*"([^"]+)"[^}]*\}/);
    if (formSubmissionMatch) {
      logger.log('🔥🔥🔥 Found form submission type:', formSubmissionMatch[1]);
    }
    
    logger.log('🔥🔥🔥 Final extracted vehicle data from comments:', { year, make, model, trim });
  }
  
  // Check for vehicle data in chatTranscription messages for Inventory
  if (!year && !make && !model && params.chatTranscription) {
    logger.log('🔥🔥🔥 Searching chat transcription for vehicle data...');
    for (const message of params.chatTranscription) {
      if (typeof message === 'object' && message.content) {
        try {
          const parsed = JSON.parse(message.content);
          if (parsed.Reply_Message?.Content?.Inventory && Array.isArray(parsed.Reply_Message.Content.Inventory) && parsed.Reply_Message.Content.Inventory.length > 0) {
            const firstVehicle = parsed.Reply_Message.Content.Inventory[0];
            year = firstVehicle.Year?.toString() || '';
            make = firstVehicle.Make || '';
            model = firstVehicle.Model || '';
            trim = firstVehicle.Version || firstVehicle.Trim || '';
            vin = firstVehicle.VIN || '';
            stock = firstVehicle.Stock_Number || '';
            status = firstVehicle.Status || '';
            logger.log('🔥🔥🔥 Vehicle data extracted from Inventory:', { year, make, model, trim, vin, stock, status });
            break;
          }
        } catch (e) {
          // Continue searching other messages
        }
      }
    }
  }
  
  // Fallback to parsing vehicleOfInterest string
  if (!year && !make && !model && params.formSubmission && params.formSubmission.vehicleOfInterest) {
    const vehicleInterest = params.formSubmission.vehicleOfInterest;
    logger.log('🔥 Parsing vehicle interest string:', vehicleInterest);
    
    // Parse vehicle string like "2025 seltos" or "2025 Kia Seltos"
    const vehicleParts = vehicleInterest.trim().split(/\s+/);
    make = 'Kia'; // Default to Kia as this appears to be a Kia dealer
    
    if (vehicleParts.length >= 2) {
      const firstPart = vehicleParts[0];
      // Check if first part is a year (4 digits)
      if (/^\d{4}$/.test(firstPart)) {
        year = firstPart;
        // Remaining parts are make/model
        if (vehicleParts.length === 2) {
          // Just year and model (e.g., "2025 Seltos")
          model = vehicleParts[1];
        } else if (vehicleParts.length >= 3) {
          // Year, make, model (e.g., "2025 Kia Seltos")
          make = vehicleParts[1];
          model = vehicleParts.slice(2).join(' ');
        }
      } else {
        // No year, assume first part is make
        make = vehicleParts[0];
        model = vehicleParts.slice(1).join(' ');
      }
    } else if (vehicleParts.length === 1) {
      // Single word, assume it's the model
      model = vehicleParts[0];
    }
  }
  
  // NEW: Enhanced chat transcription parsing for vehicle details
  if ((!make || !model || !year || !trim) && params.chatTranscription && params.chatTranscription.length > 0) {
    logger.log('🔥🔥🔥 === ENHANCED CHAT TRANSCRIPTION VEHICLE PARSING ===');
    
    // Look through recent chat messages for vehicle information
    for (let i = params.chatTranscription.length - 1; i >= 0; i--) {
      const message = params.chatTranscription[i];
      if (!message || !message.content) continue;
      
      let messageContent = '';
      try {
        // Handle both string content and parsed JSON content
        if (typeof message.content === 'string') {
          messageContent = message.content;
          
          // Try to parse as JSON if it looks like JSON
          if (message.content.trim().startsWith('{')) {
            const parsed = JSON.parse(message.content);
            messageContent = JSON.stringify(parsed); // Re-stringify for consistent parsing
          }
        } else {
          messageContent = JSON.stringify(message.content);
        }
      } catch (e) {
        messageContent = String(message.content);
      }
      
      logger.log('🔥🔥🔥 Analyzing message content:', messageContent.substring(0, 200));
      
      // Enhanced vehicle extraction patterns
      const chatVehiclePatterns = [
        // Pattern: "Kia Seltos LX 2025" or "2025 Kia Seltos LX"
        /\b(?:(\d{4})\s+)?(Kia|Toyota|Honda|Ford|Chevrolet|GMC|Hyundai|Nissan|Mazda|Subaru|Jeep|Ram|Dodge)\s+([A-Za-z][A-Za-z0-9]*(?:\s+[A-Za-z][A-Za-z0-9]*)?)\s*(?:(LX|EX|SE|Limited|Sport|Premium|S|SV|SR|XLE|LE|Base|HEV))?\s*(?:(\d{4}))?/gi,
        // JSON field patterns
        /"make":\s*"([^"]+)"/gi,
        /"model":\s*"([^"]+)"/gi,
        /"year":\s*"?(\d{4})"?/gi,
        /"trim":\s*"([^"]+)"/gi,
        /"version":\s*"([^"]+)"/gi
      ];
      
      for (const pattern of chatVehiclePatterns) {
        let match;
        pattern.lastIndex = 0;
        
        while ((match = pattern.exec(messageContent)) !== null) {
          logger.log('🔥🔥🔥 Chat pattern match:', match);
          
          // Handle main vehicle pattern
          if (match.length > 4) {
            const yearFirst = match[1];
            const makeMatch = match[2];
            const modelMatch = match[3];
            const trimMatch = match[4];
            const yearLast = match[5];
            
            year = year || yearFirst || yearLast || '';
            make = make || makeMatch || '';
            model = model || modelMatch || '';
            trim = trim || trimMatch || '';
          } else {
            // Handle JSON field patterns
            if (pattern.source.includes('make')) {
              make = make || match[1] || '';
            } else if (pattern.source.includes('model')) {
              model = model || match[1] || '';
            } else if (pattern.source.includes('year')) {
              year = year || match[1] || '';
            } else if (pattern.source.includes('trim') || pattern.source.includes('version')) {
              trim = trim || match[1] || '';
            }
          }
          
          logger.log('🔥🔥🔥 Updated from chat message:', { year, make, model, trim });
        }
      }
      
      // Stop if we have sufficient vehicle data
      if (make && model && year) {
        logger.log('🔥🔥🔥 Found sufficient vehicle data from chat, stopping search');
        break;
      }
    }
    
    logger.log('🔥🔥🔥 Final vehicle data from chat transcription:', { year, make, model, trim });
  }

  // FINAL STATUS DETERMINATION - Phase 5: year/mileage overrides win over source-page hints.
  if (year) {
    const currentYear = new Date().getFullYear();
    const vehicleYear = parseInt(year);
    const mileageNum = mileage ? parseFloat(mileage.toString().replace(/[^\d.]/g, '')) : NaN;

    if (vehicleYear && vehicleYear > currentYear) {
      status = 'new';
      logger.log(`🔥🔥🔥 FINAL STATUS OVERRIDE: Year ${vehicleYear} > ${currentYear} = new (wins over Used/Occasion)`);
    } else if (vehicleYear && vehicleYear === currentYear && (!mileage || Number.isNaN(mileageNum) || mileageNum < 200)) {
      status = 'new';
      logger.log(`🔥🔥🔥 FINAL STATUS OVERRIDE: Year ${vehicleYear} = ${currentYear} with mileage < 200 = new (wins over Used/Occasion)`);
    } else if (vehicleYear && vehicleYear < currentYear) {
      status = 'used';
      logger.log(`🔥🔥🔥 FINAL STATUS: Year ${vehicleYear} < ${currentYear} = used`);
    } else if (!status && vehicleYear === currentYear) {
      status = 'new';
      logger.log(`🔥🔥🔥 FINAL STATUS: Year ${vehicleYear} = ${currentYear} = new`);
    } else if (!status) {
      status = 'used'; // Default for unknown years
      logger.log(`🔥🔥🔥 FINAL STATUS: Unknown year, defaulting to used`);
    }
  }

  // SAFEGUARD: If we extracted a make/model but it doesn't look like a real vehicle
  // (e.g. scraped legal text from page context), strip the vehicle fields so the ADF
  // goes out with an empty <vehicle> placeholder instead of polluting the CRM.
  if ((make || model) && !isPlausibleVehicle(make, model)) {
    logger.warn('🛑 ADF VEHICLE SAFEGUARD: Rejecting implausible vehicle data', { year, make, model, trim, vin, stock });
    logger.warn('ADF vehicle safeguard triggered — clearing implausible vehicle fields', { make, model });
    try {
      toast.error("We couldn't capture your vehicle of interest. Your request was sent — a specialist will follow up.");
    } catch (_) { /* toast may be unavailable in non-DOM contexts */ }
    year = '';
    make = '';
    model = '';
    trim = '';
    vin = '';
    stock = '';
    price = '';
    mileage = '';
    color = '';
  }

  // ============================================================
  // DETERMINISTIC INVENTORY ENRICHMENT (stock # + owning dealer)
  // The AI two-hop tool chain almost never supplies these, so we fetch them
  // directly from live inventory by VIN. Strictly best-effort: never blocks.
  // ============================================================
  let ownerDealerId = '';
  try {
    const vinCandidate = (vin || '').trim();
    if (/^[A-HJ-NPR-Z0-9]{17}$/i.test(vinCandidate)) {
      const enrichDealerId = await getDealerID();
      if (enrichDealerId && enrichDealerId !== 0) {
        const { supabase } = await import('@/integrations/supabase/client');
        const invokePromise = supabase.functions.invoke('get-vehicle-details', {
          body: { dealer_id: enrichDealerId, vin: vinCandidate }
        });
        // Swallow any late rejection so a timed-out race doesn't produce an unhandled rejection
        const guardedInvokePromise = invokePromise.catch(() => undefined);
        const TIMEOUT = Symbol('timeout');
        const raced: any = await Promise.race([
          guardedInvokePromise,
          new Promise((resolve) => setTimeout(() => resolve(TIMEOUT), 3000))
        ]);


        if (raced === TIMEOUT) {
          logger.log('🔥🔥🔥 ⏱️ ADF inventory enrichment timed out (3s) - continuing without stock/owner');
        } else if (raced?.error) {
          logger.log('🔥🔥🔥 ⚠️ ADF inventory enrichment failed:', raced.error?.message || raced.error);
        } else {
          const vehicle = raced?.data;
          if (vehicle && !vehicle.error) {
            const rawStock = vehicle['stock number'];
            if (!stock && rawStock) {
              stock = String(rawStock).trim();
              logger.log('🔥🔥🔥 ✅ ADF enrichment: stock number resolved from inventory');
            }
            if (vehicle.owner !== undefined && vehicle.owner !== null && String(vehicle.owner).trim() !== '') {
              ownerDealerId = String(vehicle.owner).trim();
              logger.log('🔥🔥🔥 ✅ ADF enrichment: owner dealer ID resolved from inventory');
            }
          } else {
            logger.log('🔥🔥🔥 ⚠️ ADF inventory enrichment: vehicle not found for VIN');
          }
        }
      } else {
        logger.log('🔥🔥🔥 ADF inventory enrichment skipped - no dealer ID');
      }
    }
  } catch (enrichError) {
    logger.log('🔥🔥🔥 ⚠️ ADF inventory enrichment error (ignored):', enrichError);
  }

  // Always generate vehicle XML - either with data or as placeholder
  if (year || make || model) {


    // Map status values to ADF format
    let adfStatus = 'used'; // Default to used for safety
    if (status) {
      const statusLower = status.toLowerCase();
      if (statusLower === 'used' || statusLower === 'occasion') {
        adfStatus = 'used';
      } else if (statusLower === 'demo' || statusLower === 'démo') {
        adfStatus = 'demo';
      } else if (statusLower === 'new' || statusLower === 'neuf') {
        adfStatus = 'new';
      }
    }
    
    // Determine vehicle interest and status based on form type - CRITICAL for CRM routing
    let vehicleInterest = 'buy'; // Default for most forms
    let finalStatus = adfStatus; // Use calculated status by default
    
    if (params.formType?.toLowerCase() === 'service') {
      vehicleInterest = 'service';
      finalStatus = 'service';
      logger.log('🔥🔥🔥 Service form detected - setting vehicle interest="service" status="service" for CRM routing');
    } else {
      logger.log('🔥🔥🔥 Non-service form - using default vehicle interest "buy"');
    }
    
    logger.log('🔥🔥🔥 Creating vehicle XML with:', { year, make, model, trim, vin, stock, price, mileage, color, status, adfStatus, finalStatus, vehicleInterest, formType: params.formType });
    
    // Create proper colorcombination structure
    let colorcombinationXml = '';
    if (params.formSubmission) {
      const exteriorColor = params.formSubmission['Exterior Color'] || params.formSubmission.Color || params.formSubmission.color || '';
      const interiorColor = params.formSubmission['Interior Color'] || params.formSubmission.interiorColor || '';
      
      if (exteriorColor || interiorColor) {
        colorcombinationXml = `<colorcombination>
        <interiorcolor>${escapeXml(interiorColor)}</interiorcolor>
        <exteriorcolor>${escapeXml(exteriorColor)}</exteriorcolor>
        <preference></preference>
      </colorcombination>`;
      }
    }
    
    const vehicleElements = [
      year ? `<year>${escapeXml(year)}</year>` : '',
      make ? `<make>${escapeXml(make)}</make>` : '',
      model ? `<model>${escapeXml(model)}</model>` : '',
      trim ? `<trim>${escapeXml(trim)}</trim>` : '',
      vin ? `<vin>${escapeXml(vin)}</vin>` : '',
      stock ? `<stock>${escapeXml(stock)}</stock>` : '',
      ownerDealerId ? `<id sequence="1" source="OwnerDealerID">${escapeXml(String(ownerDealerId))}</id>` : '',
      price ? `<price>${escapeXml(price.toString().replace(/[^\d.]/g, ''))}</price>` : '',
      colorcombinationXml,

      mileage ? `<odometer status="original" units="km">${escapeXml(mileage.toString().replace(/[^\d.]/g, ''))}</odometer>` : ''
    ].filter(Boolean).join('\n      ');
    
    vehiclesXml = `
    <vehicle interest="${vehicleInterest}" status="${finalStatus}">
      ${vehicleElements}
    </vehicle>`;
    logger.log('🔥🔥🔥 Generated vehicle XML:', vehiclesXml);
  } else {
    // Determine vehicle interest and status for placeholder based on form type
    let vehicleInterest = 'buy'; // Default
    let placeholderStatus = 'new'; // Default
    
    if (params.formType?.toLowerCase() === 'service') {
      vehicleInterest = 'service';
      placeholderStatus = 'service';
      logger.log('🔥🔥🔥 Service form placeholder - setting vehicle interest="service" status="service" for CRM routing');
    }
    
    logger.log('🔥🔥🔥 No vehicle data found, creating placeholder vehicle XML with interest:', vehicleInterest, 'status:', placeholderStatus);
    // Create a placeholder vehicle object for forms without vehicle data
    vehiclesXml = `
    <vehicle interest="${vehicleInterest}" status="${placeholderStatus}">
    </vehicle>`;
  }
  
  // Fallback to existing vehicle handling if no vehicle data found
  if (!vehiclesXml && params.vehicles && params.vehicles.length > 0) {
    // Fallback to existing vehicle handling
    params.vehicles.forEach((vehicle, index) => {
      vehiclesXml += `
    <vehicle interest="${vehicle.interest}" status="${vehicle.status}">`;
      
      if (vehicle.id) {
        vehiclesXml += `
      <id sequence="${index + 1}" source="Stock#">${escapeXml(vehicle.id)}</id>`;
      }
      
      if (vehicle.year) {
        vehiclesXml += `
      <year>${escapeXml(String(vehicle.year))}</year>`;
      }
      
      if (vehicle.vehicletype) {
        vehiclesXml += `
      <vehicletype>${escapeXml(vehicle.vehicletype)}</vehicletype>`;
      }
      
      if (vehicle.make) {
        vehiclesXml += `
      <make>${escapeXml(vehicle.make)}</make>`;
      }
      
      if (vehicle.model) {
        vehiclesXml += `
      <model>${escapeXml(vehicle.model)}</model>`;
      }
      
      if (vehicle.trim) {
        vehiclesXml += `
      <trim>${escapeXml(vehicle.trim)}</trim>`;
      }
      
      if (vehicle.vin) {
        vehiclesXml += `
      <vin>${escapeXml(vehicle.vin)}</vin>`;
      }
      
      if (vehicle.stock) {
        vehiclesXml += `
      <stock>${escapeXml(vehicle.stock)}</stock>`;
      }
      
      if (vehicle.price) {
        vehiclesXml += `
      <price>${escapeXml(String(vehicle.price))}</price>`;
      }
      
      if (vehicle.condition) {
        vehiclesXml += `
      <condition>${escapeXml(vehicle.condition)}</condition>`;
      }
      
      if (vehicle.odometer) {
        vehiclesXml += `
      <odometer status="${vehicle.odometer.status}" units="${vehicle.odometer.units}">${escapeXml(String(vehicle.odometer.value))}</odometer>`;
      }
      
      vehiclesXml += `
    </vehicle>`;
    });
  }

  // Parse customer address from form submission
  let street = '';
  let city = '';
  let regionCode = '';
  let postalCode = '';
  let country = params.customer.address?.country || 'CA';
  
  if (params.formSubmission && params.formSubmission.address) {
    const address = params.formSubmission.address;
    logger.log('🔥 Parsing address:', address);
    
    // Parse address like "1515 Bd Chomedey, Laval, QC H7V 3Y7, Canada"
    const addressParts = address.split(',').map((part: string) => part.trim());
    
    if (addressParts.length >= 4) {
      street = addressParts[0];
      city = addressParts[1];
      const statePostal = addressParts[2].trim();
      // Extract province/state and postal code from "QC H7V 3Y7"
      const statePostalMatch = statePostal.match(/^([A-Z]{2})\s+([A-Z0-9\s]+)$/);
      if (statePostalMatch) {
        regionCode = statePostalMatch[1];
        postalCode = statePostalMatch[2].replace(/\s+/g, ' ').trim();
      }
      country = addressParts[3] === 'Canada' ? 'CA' : addressParts[3];
    } else if (addressParts.length >= 2) {
      street = addressParts[0];
      city = addressParts[1];
    }
  }

  // Build comprehensive comments in markdown format for CRM users
  let comments = buildCRMComments(params, language);
  logger.log('🔥🔥🔥 Built comprehensive CRM comments');
  
  // Convert French characters to HTML entities for proper display in ISO-8859-1 systems
  logger.log('📝 COMMENTS BEFORE ENCODING:', comments);
  comments = encodeCommentsForISO88591(comments);
  logger.log('📝 COMMENTS AFTER ENCODING (with HTML entities):', comments);

  // Read UTM cookies for campaign/source fields (not included in comments)
  const utmSource = getCookie('utm_source');
  const utmCampaign = getCookie('utm_campaign');

  const capitalizedFormType = capitalizeFormType(params.formType || 'Unknown');

  const adfXml = `<?xml version="1.0" encoding="UTF-8"?>
<?ADF version="1.0"?>
<adf>
  <prospect>
    <id source="language">${escapeXml(language)}</id>${params.prospect.id ? `
    <id source="sessionid">${escapeXml(params.prospect.id)}</id>` : ''}
    <requestdate>${requestDate}</requestdate>
    <customer>
      <contact>
        ${params.customer.name ? `<name part="full">${escapeXml(params.customer.name)}</name>` : ''}${params.customer.phone ? `
        <phone type="voice" time="" preferredcontact="1">${escapeXml(params.customer.phone)}</phone>
        <mandatory_tel>Yes</mandatory_tel>` : ''}${(params.customer.email || params.formSubmission?.email) ? `
        <email preferredcontact="1">${escapeXml(params.customer.email || params.formSubmission?.email)}</email>` : ''}
        <address>
          ${street ? `<street line="1">${escapeXml(street)}</street>` : ''}
          ${city ? `<city>${escapeXml(city)}</city>` : ''}
          ${regionCode ? `<regioncode>${escapeXml(regionCode)}</regioncode>` : ''}
          ${postalCode ? `<postalcode>${escapeXml(postalCode)}</postalcode>` : ''}
          <country>${escapeXml(country)}</country>
        </address>
      </contact>
      ${params.formSubmission && params.formSubmission.dateOfBirth ? `<birthdate>${escapeXml(params.formSubmission.dateOfBirth)}</birthdate>` : ''}
      ${params.formSubmission && params.formSubmission.annualIncome ? `<income annual="${escapeXml(params.formSubmission.annualIncome)}" />` : ''}
      ${params.formSubmission && params.formSubmission.monthlyBudget ? `<finance monthly_payment="${escapeXml(params.formSubmission.monthlyBudget)}" />` : ''}
      <comments><![CDATA[${comments}]]></comments>
    </customer>${vehiclesXml}
    ${utmCampaign ? `<campaign>${escapeXml(utmCampaign)}</campaign>` : ''}
    ${utmSource ? `<source>${escapeXml(utmSource)}</source>` : ''}
    <vendor>
      <vendorname>${escapeXml(dealerData.dealer_name || dealerData.domain || 'Unknown Dealer')}</vendorname>
      <vehiclelocation>${escapeXml(dealerData.dealer_name || dealerData.domain || 'Unknown Dealer')}</vehiclelocation>
      <contact primarycontact="1">
        <name part="full">${escapeXml(dealerData.dealer_name || dealerData.domain || 'Dealer Contact')}</name>
        ${dealerData.send_adf_leads_to ? `<email>${escapeXml(dealerData.send_adf_leads_to)}</email>` : ''}
        <formid>D2C Chatbot ${escapeXml(capitalizedFormType)}</formid>
      </contact>
    </vendor>
    <provider>
      <name part="full">D2C Media</name>
      <form>D2C Chatbot ${escapeXml(capitalizedFormType)}</form>
      <service>D2C Media - Lead : Chatbot Boost ${escapeXml(capitalizedFormType)}</service>
      <url>https://d2cmedia.ca</url>
    </provider>
  </prospect>
</adf>`;

  return adfXml;
};

const escapeXml = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Convert French and special characters to HTML entities for ISO-8859-1 compatibility
const encodeCommentsForISO88591 = (text: string): string => {
  if (!text) return '';
  
  const charMap: { [key: string]: string } = {
    'à': '&agrave;', 'À': '&Agrave;',
    'â': '&acirc;', 'Â': '&Acirc;',
    'é': '&eacute;', 'É': '&Eacute;',
    'è': '&egrave;', 'È': '&Egrave;',
    'ê': '&ecirc;', 'Ê': '&Ecirc;',
    'ë': '&euml;', 'Ë': '&Euml;',
    'î': '&icirc;', 'Î': '&Icirc;',
    'ï': '&iuml;', 'Ï': '&Iuml;',
    'ô': '&ocirc;', 'Ô': '&Ocirc;',
    'ù': '&ugrave;', 'Ù': '&Ugrave;',
    'û': '&ucirc;', 'Û': '&Ucirc;',
    'ü': '&uuml;', 'Ü': '&Uuml;',
    'ç': '&ccedil;', 'Ç': '&Ccedil;',
    'œ': '&oelig;', 'Œ': '&OElig;',
    'æ': '&aelig;', 'Æ': '&AElig;',
    '\u2019': '&rsquo;', '\u2018': '&lsquo;',
    '\u201C': '&ldquo;', '\u201D': '&rdquo;',
    '\u2013': '&ndash;', '\u2014': '&mdash;',
    '€': '&euro;'
  };
  
  let result = text;
  for (const [char, entity] of Object.entries(charMap)) {
    result = result.replace(new RegExp(char, 'g'), entity);
  }
  
  return result;
};
